import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import { WorkItem } from '../components/workitems/workItemTypes';
import { searchWorkItemHistory, PaginatedWorkItems } from '../services/workItemDataService';
import { useUsers } from '../hooks/useUsers';
import { getRsLineItemsChargeableTotal } from '../components/workitems/utils/workItemUtils';
import { SmartSelect, DateField } from '../components/dropdown';
import { makeTechnicianRenderItem } from '../components/calendar/renderTechnicianOption';
import { buildUserColorIndex } from '../utils/userColorIndex';
import './WorkItemSearchPage.css';

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface WorkItemSearchPageProps {
  userInfo?: UserInfo | null;
}

interface SearchFields {
  name: boolean;
  subject: boolean;
  comments: boolean;
}

export const WorkItemSearchPage: React.FC<WorkItemSearchPageProps> = ({ userInfo }) => {
  const navigate = useNavigate();
  const { users: cachedUsers } = useUsers();

  const [searchText, setSearchText] = useState('');
  const [searchFields, setSearchFields] = useState<SearchFields>({
    name: true,
    subject: true,
    comments: false,
  });
  const [ticketNumber, setTicketNumber] = useState('');
  const [technician, setTechnician] = useState<string | null>(userInfo?.displayName ?? null);
  const [resolvedFrom, setResolvedFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 5);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [resolvedTo, setResolvedTo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [results, setResults] = useState<WorkItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const initialSearchApplied = useRef(false);
  const isAdmin = userInfo?.roles.includes('admin') ?? false;

  const technicianNames = useMemo(() => {
    const names = new Set(cachedUsers.map(u => u.fullname));
    return Array.from(names).filter(Boolean).sort();
  }, [cachedUsers]);

  const renderTechnicianItem = useMemo(
    () => makeTechnicianRenderItem(buildUserColorIndex(cachedUsers)),
    [cachedUsers],
  );

  const anyFieldChecked = searchFields.name || searchFields.subject || searchFields.comments;
  const canSearch = Boolean(
    (searchText.trim() && anyFieldChecked) || technician || resolvedFrom || resolvedTo,
  );

  const buildSearchFieldsParam = (): string | undefined => {
    if (!searchText.trim()) return undefined;
    const active = Object.entries(searchFields)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(',');
    return active || undefined;
  };

  const fetchResults = useCallback(
    (currentPage: number, currentPageSize: number) => {
      const controller = new AbortController();
      setLoading(true);
      setError(null);

      searchWorkItemHistory(
        {
          search: searchText.trim() || undefined,
          searchFields: buildSearchFieldsParam(),
          technician: technician || undefined,
          resolvedFrom: resolvedFrom || undefined,
          resolvedTo: resolvedTo || undefined,
          includeDeleted: includeDeleted || undefined,
          page: currentPage + 1, // API is 1-based
          pageSize: currentPageSize,
        },
        controller.signal,
      )
        .then((data: PaginatedWorkItems) => {
          setResults(data.items);
          setTotalItems(data.pagination.totalItems);
          setHasSearched(true);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') setError(err.message);
        })
        .finally(() => setLoading(false));

      return controller;
    },
    [searchText, searchFields, technician, resolvedFrom, resolvedTo, includeDeleted], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Pick up search term passed from the dashboard and auto-search
  useEffect(() => {
    if (initialSearchApplied.current) return;
    const stored = sessionStorage.getItem('workitem_search_term');
    if (stored) {
      sessionStorage.removeItem('workitem_search_term');
      initialSearchApplied.current = true;
      setSearchText(stored);
      setLoading(true);
      setError(null);
      searchWorkItemHistory({ search: stored, searchFields: 'name,subject', page: 1, pageSize: rowsPerPage })
        .then((data: PaginatedWorkItems) => {
          setResults(data.items);
          setTotalItems(data.pagination.totalItems);
          setHasSearched(true);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when page/rowsPerPage change (after initial search)
  useEffect(() => {
    if (!hasSearched) return;
    const controller = fetchResults(page, rowsPerPage);
    return () => controller.abort();
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    if (!canSearch) return;
    setPage(0);
    fetchResults(0, rowsPerPage);
  };

  const handleClear = () => {
    setSearchText('');
    setSearchFields({ name: true, subject: true, comments: false });
    setTicketNumber('');
    setTechnician(userInfo?.displayName ?? null);
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
    setResolvedFrom(`${fiveDaysAgo.getFullYear()}-${String(fiveDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(fiveDaysAgo.getDate()).padStart(2, '0')}`);
    const today = new Date();
    setResolvedTo(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
    setIncludeDeleted(false);
    setResults([]);
    setTotalItems(0);
    setHasSearched(false);
    setError(null);
  };

  const handleTicketGo = () => {
    const num = ticketNumber.trim();
    if (num && /^\d+$/.test(num)) navigate(`/ticket/${num}`);
  };

  // At least one search field must stay selected.
  const handleFieldToggle = (field: keyof SearchFields) => {
    setSearchFields((prev) => {
      const next = { ...prev, [field]: !prev[field] };
      if (!next.name && !next.subject && !next.comments) return prev;
      return next;
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Pagination display
  const rangeStart = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, totalItems);
  const rangeLabel = `${rangeStart}–${rangeEnd} of ${totalItems}`;
  const hasNextPage = (page + 1) * rowsPerPage < totalItems;

  return (
    <div className="search-page">
      {/* Filter card — two explicit non-wrapping rows. */}
      <div className="search-filter">
        {/* Row 1 — keyword · field toggles · actions */}
        <div className="search-filter__row1">
          <div className="search-filter__keyword-wrap">
            <IconSearch className="search-filter__keyword-icon" size={16} stroke={1.5} />
            <input
              className="search-filter__keyword"
              placeholder="Search…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>

          <div className="search-filter__vsep" />

          <span className="search-filter__label">Search in:</span>

          {([
            ['name', 'Name / Business'],
            ['subject', 'Subject'],
            ['comments', 'Comments'],
          ] as [keyof SearchFields, string][]).map(([key, label]) => (
            <label
              key={key}
              className={`search-filter__cb ${searchFields[key] ? 'on' : ''}`}
            >
              <input
                type="checkbox"
                checked={searchFields[key]}
                onChange={() => handleFieldToggle(key)}
              />
              {label}
            </label>
          ))}

          <div className="search-filter__spacer" />

          <button
            type="button"
            className="search-filter__btn-search"
            onClick={handleSearch}
          >
            <IconSearch size={16} stroke={1.5} /> Search
          </button>
          <button
            type="button"
            className="search-filter__btn-clear"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>

        {/* Row 2 — refinements · ticket jump */}
        <div className="search-filter__row2">
          <span className="search-filter__label">Technician:</span>
          <div className="search-filter__tech">
            <SmartSelect
              label="Technician"
              value={technician}
              options={technicianNames.map((name) => ({ value: name, label: name }))}
              onChange={setTechnician}
              placeholder="All technicians"
              renderItem={renderTechnicianItem}
            />
          </div>

          <div className="search-filter__vsep" />

          <span className="search-filter__label">From</span>
          <div className="search-filter__date-field">
            <DateField
              label="From"
              value={resolvedFrom || null}
              onChange={(iso) => setResolvedFrom(iso ?? '')}
            />
          </div>

          <span className="search-filter__label">To</span>
          <div className="search-filter__date-field">
            <DateField
              label="To"
              value={resolvedTo || null}
              onChange={(iso) => setResolvedTo(iso ?? '')}
            />
          </div>

          {isAdmin && (
            <>
              <div className="search-filter__vsep" />
              <label className="search-filter__cb">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                Show deleted
              </label>
            </>
          )}

          <div className="search-filter__spacer" />

          <div className="search-filter__ticket-wrap">
            <input
              placeholder="Jump to ticket #"
              value={ticketNumber}
              inputMode="numeric"
              onChange={(e) => setTicketNumber(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleTicketGo()}
            />
            <button type="button" onClick={handleTicketGo}>Go</button>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && <div className="search-error">{error}</div>}

      {/* ── Loading ── */}
      {loading && <div className="search-state">Searching…</div>}

      {/* ── Results ── */}
      {!loading && hasSearched && (
        <div className="search-results">
          <table className="search-table">
            <thead>
              <tr>
                <th className="search-table__hdr">Job #</th>
                <th className="search-table__hdr">Title</th>
                <th className="search-table__hdr">Customer</th>
                <th className="search-table__hdr">Technician</th>
                <th className="search-table__hdr">Status</th>
                <th className="search-table__hdr">Cost</th>
                <th className="search-table__hdr">Resolved</th>
                <th className="search-table__hdr"></th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td className="search-table__empty" colSpan={8}>
                    No resolved items found for the selected criteria.
                  </td>
                </tr>
              ) : (
                results.map((item) => {
                  const cost = getRsLineItemsChargeableTotal(item);
                  const paid = item.invoices?.some((inv) => inv.is_paid) ?? false;
                  return (
                    <tr key={item.guid || item.id} className="search-table__row">
                      <td className="search-table__cell search-table__cell--ticket">
                        {item.number ? <a href={`/ticket/${item.number}`}>{item.number}</a> : '-'}
                      </td>
                      <td className="search-table__cell">{item.title}</td>
                      <td className="search-table__cell">
                        {item.customerBusinessName
                          ? `${item.customerName} (${item.customerBusinessName})`
                          : item.customerName}
                      </td>
                      <td className="search-table__cell search-table__cell--muted">{item.userFullName || '-'}</td>
                      <td className="search-table__cell search-table__cell--muted" style={{ textTransform: 'capitalize' }}>
                        {item.status}
                      </td>
                      <td className={`search-table__cell${paid ? ' search-table__cell--paid' : ''}`}>
                        {cost === null ? '-' : `$${cost.toFixed(2)}`}
                      </td>
                      <td className="search-table__cell search-table__cell--muted">
                        {formatDate(item.resolvedAt || item.updatedAt)}
                      </td>
                      <td className="search-table__cell">
                        {item.id && (
                          <a
                            className="search-table__rs-tag"
                            href={`https://techsolvers.repairshopr.com/tickets/${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in RepairShopr"
                          >
                            RS
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {results.length > 0 && (
            <div className="search-table__footer">
              <span>Rows per page:</span>
              <select
                className="search-table__rpp"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>{rangeLabel}</span>
              <button
                type="button"
                className="search-table__page-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <IconChevronLeft size={16} stroke={1.5} />
              </button>
              <button
                type="button"
                className="search-table__page-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasNextPage}
              >
                <IconChevronRight size={16} stroke={1.5} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Initial state ── */}
      {!loading && !hasSearched && !error && (
        <p className="search-empty-state">
          Enter a search term or apply filters to find work items.
        </p>
      )}
    </div>
  );
};
